/**
 * @file ImportTemplatesPage.js
 * @description Manages the display and interactions within the Import Templates page.
 */

import { useState, useEffect } from 'react';
import ImportTemplateList from './ImportTemplateList';
import AddEditImportTemplateForm from './AddEditImportTemplateForm';
import style from './ImportTemplates.module.scss';
import {
    getAllImportTemplatesForTenant,
    getImportTemplate,
    createImportTemplate,
    updateImportTemplate,
    deleteImportTemplate,
} from '../../services/importService';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { getNameForDuplicate } from '../../utils/utils';
import { getText } from '../../i18n';

const normalizeCopiedTemplateField = (templateField = {}, index = 0) => ({
    id: 0,
    importTemplateId: 0,
    importTemplateTypeId: 0,
    importFieldId: templateField.importFieldId ?? null,
    columnNumber: templateField.columnNumber ?? index + 1,
    startingLocation: templateField.startingLocation ?? 0,
    length: templateField.length ?? 0,
});

const normalizeCopiedTranslationMap = (translationMap = {}, index = 0) => ({
    id: 0,
    processingOrder: translationMap.processingOrder ?? index + 1,
    from: translationMap.else ? null : (translationMap.from ?? ''),
    to: translationMap.to ?? '',
    else: !!translationMap.else,
});

const normalizeCopiedFormulaMapEvaluation = evaluation => ({
    id: 0,
    leftExpression: evaluation?.leftExpression ?? '',
    rightExpression: evaluation?.rightExpression ?? '',
    operatorId: evaluation?.operatorId ?? null,
});

const normalizeCopiedFormulaMap = formulaMap => {
    if (!formulaMap) {
        return null;
    }

    return {
        id: 0,
        value: formulaMap.value ?? '',
        evaluations: (formulaMap.evaluations || []).map(evaluation =>
            normalizeCopiedFormulaMapEvaluation(evaluation),
        ),
    };
};

const normalizeCopiedImportMapGroup = (importMapGroup = {}, index = 0) => ({
    id: 0,
    importMapGroupTypeId: importMapGroup.importMapGroupTypeId ?? 1,
    importTemplateTypeId: 0,
    importTemplateId: 0,
    processingOrder: importMapGroup.processingOrder ?? index + 1,
    errorMessage: importMapGroup.errorMessage ?? '',
    warningMessage: importMapGroup.warningMessage ?? '',
    importFieldId: importMapGroup.importFieldId ?? 0,
    translationMaps: (importMapGroup.translationMaps || []).map(
        (translationMap, translationIndex) =>
            normalizeCopiedTranslationMap(translationMap, translationIndex),
    ),
    formulaMap: normalizeCopiedFormulaMap(importMapGroup.formulaMap),
});

const normalizeCopiedTemplateType = (templateType = {}, index = 0) => ({
    id: 0,
    importTemplateId: 0,
    importTypeId: templateType.importTypeId ?? null,
    name: (templateType.name || '').trim(),
    processingOrder: templateType.processingOrder ?? index + 1,
    templateFields: (templateType.templateFields || []).map(
        (templateField, fieldIndex) =>
            normalizeCopiedTemplateField(templateField, fieldIndex),
    ),
    importMapGroups: (templateType.importMapGroups || []).map(
        (importMapGroup, mapGroupIndex) =>
            normalizeCopiedImportMapGroup(importMapGroup, mapGroupIndex),
    ),
});

const buildCopiedTemplatePayload = templateData => {
    const orderedTemplateTypes = [...(templateData.templateTypes || [])].sort(
        (a, b) => (a.processingOrder ?? 0) - (b.processingOrder ?? 0),
    );

    const templateTypes = orderedTemplateTypes.map((templateType, index) =>
        normalizeCopiedTemplateType(templateType, index),
    );

    return {
        id: 0,
        tenantId: templateData.tenantId ?? 0,
        name: (templateData.name || '').trim(),
        customDelimiter: templateData.customDelimiter ?? '',
        hasHeaderRecord: !!templateData.hasHeaderRecord,
        importDelimiterId: templateData.importDelimiterId ?? null,
        importFileFormatId: templateData.importFileFormatId ?? 0,
        hasTextQualifier: !!templateData.hasTextQualifier,
        sheetName: templateData.sheetName ?? '',
        importTypeId:
            templateTypes[0]?.importTypeId ?? templateData.importTypeId ?? null,
        templateTypes,
    };
};

const ImportTemplatesPage = () => {
    const [templates, setTemplates] = useState([]);
    const [addEditNewTemplate, setAddEditNewTemplate] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setSelectedTemplateId(null);
        loadImportTemplates();
    }, []);

    async function loadImportTemplates() {
        let fetchedTemplates = [];
        try {
            fetchedTemplates = await getAllImportTemplatesForTenant();
        } catch (e) {
            Toast.error(getText('msg_50245a39e403'));
        } finally {
            setIsLoading(false);
            setTemplates(fetchedTemplates);
        }
    }

    const handleAddNewTemplate = async newTemplate => {
        try {
            let savedTemplate;

            if (selectedTemplateId) {
                savedTemplate = await updateImportTemplate({
                    ...newTemplate,
                    id: selectedTemplateId,
                });
                Toast.success(getText('msg_54d9b112fe2e'));
            } else {
                savedTemplate = await createImportTemplate(newTemplate);
                Toast.success(getText('msg_1c993e6557c4'));

                if (savedTemplate?.id) {
                    setSelectedTemplateId(savedTemplate.id);
                }
            }

            await loadImportTemplates();
            return savedTemplate;
        } catch (error) {
            Toast.error(getText('msg_e7c8a61a02cd'));
            throw error;
        }
    };

    const handleDeleteTemplates = async templateIds => {
        const deletePromises = templateIds.map(deleteImportTemplate);
        const results = await Promise.allSettled(deletePromises);

        const successfulDeletions = [];
        const failedDeletions = [];

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                successfulDeletions.push(templateIds[index]);
            } else {
                failedDeletions.push(templateIds[index]);
            }
        });

        if (successfulDeletions.length && failedDeletions.length) {
            Toast.error(getText('msg_504e85eddfc2'));
        }

        if (successfulDeletions.length && !failedDeletions.length) {
            Toast.success(getText('msg_693d45ced594'));
        }

        loadImportTemplates();
    };

    const handleCopyTemplates = async templateIds => {
        if (!templateIds.length) {
            Toast.error(getText('msg_b244a580f588'));
            return;
        }

        setIsLoading(true);

        try {
            const results = await Promise.allSettled(
                templateIds.map(async templateId => {
                    const originalTemplate =
                        await getImportTemplate(templateId);

                    const existingNames = templates.map(
                        template => template.name,
                    );
                    const newTemplateName = getNameForDuplicate(
                        originalTemplate.name,
                        existingNames,
                    );

                    const copiedTemplatePayload = buildCopiedTemplatePayload({
                        ...originalTemplate,
                        name: newTemplateName,
                    });

                    await createImportTemplate(copiedTemplatePayload);
                    return originalTemplate.name;
                }),
            );

            const successfulCopies = results.filter(
                result => result.status === 'fulfilled',
            );
            const failedCopies = results.filter(
                result => result.status === 'rejected',
            );

            if (successfulCopies.length > 0 && failedCopies.length === 0) {
                Toast.success(
                    successfulCopies.length === 1
                        ? getText('msg_1843fd640019')
                        : getText('msg_6e6dcd54e235', {
                              count: successfulCopies.length,
                          }),
                );
            }

            if (successfulCopies.length > 0 && failedCopies.length > 0) {
                Toast.error(
                    getText('msg_bbf3817b51e8', {
                        successCount: successfulCopies.length,
                        failedCount: failedCopies.length,
                    }),
                );
            }

            if (successfulCopies.length === 0 && failedCopies.length > 0) {
                Toast.error(getText('msg_0c9645ba4927'));
            }
        } finally {
            await loadImportTemplates();
        }
    };

    const showAddNewTemplateForm = id => {
        setSelectedTemplateId(id);
        setAddEditNewTemplate(true);
    };

    const returnToList = () => {
        setSelectedTemplateId(null);
        setAddEditNewTemplate(false);
    };

    const renderContent = () => {
        if (addEditNewTemplate) {
            return (
                <AddEditImportTemplateForm
                    onAddEditTemplate={handleAddNewTemplate}
                    onReturnToList={returnToList}
                    templateId={selectedTemplateId}
                />
            );
        } else {
            return isLoading ? (
                <Spinner width={50} className={style.loadingSpinner} />
            ) : (
                <ImportTemplateList
                    templates={templates}
                    onAddEditTemplate={showAddNewTemplateForm}
                    onDeleteSelectedTemplates={handleDeleteTemplates}
                    onCopySelectedTemplates={handleCopyTemplates}
                />
            );
        }
    };

    return <div className="page-content">{renderContent()}</div>;
};

export default ImportTemplatesPage;
